'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Eases a number towards its target instead of letting it snap.
 *
 * The progress bar itself glides (CSS transition on the indicator), but the
 * percentage caption next to it is text: when the render finishes and the
 * overlay pins the bar at 100%, the digits jumped (93% → 100%) while the bar
 * slid — the one hard cut in an otherwise continuous completion. This tween
 * gives the digits the same clock as the bar.
 *
 * Only rises: progress never walks backwards on screen, and a fresh job
 * (target below the current value by more than a rounding step) re-seeds
 * instantly rather than counting down.
 */
export function useTweenedNumber(target: number, durationMs: number): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const startRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // A drop means a new job (or a reset) — snap, don't animate downwards.
    if (target < value - 1) {
      setValue(target);
      return undefined;
    }
    if (target === value) return undefined;

    fromRef.current = value;
    startRef.current = performance.now();

    const step = (now: number) => {
      const t = Math.min(1, (now - startRef.current) / durationMs);
      // ease-out cubic — matches the bar's ease-out settle.
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(fromRef.current + (target - fromRef.current) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // `value` is intentionally excluded: it is the animation's own output and
    // re-running on it would restart the tween on every frame.
  }, [target, durationMs]);

  return value;
}
